import type { Emulator } from "../emulator.js";
import { compileTrackerPreview, compileTrackerProject, type CompiledTrackerSong } from "./compiler.js";
import {
  createTrackerStatusBlock,
  readTrackerEmulatorStatus,
  TRACKER_BYTECODE_ADDRESS,
  TRACKER_BYTECODE_MAX_SIZE,
  TRACKER_PLAYER_ENTRY,
  TRACKER_STATUS_ADDRESS,
  TRACKER_STATUS_STATE,
  TRACKER_WORKSPACE_END,
  type TrackerEmulatorStatus
} from "./emulator-status.js";
import { instrumentCtrlValue, type TrackerInstrument, type TrackerProject } from "./model.js";
import { noteCodeToFrequencyRegister } from "./note-data.js";

const AUD_V1_FREQ_LO = 0xd400;
const AUD_V1_PW_LO = 0xd402;
const AUD_V1_PW_HI = 0xd403;
const AUD_V1_CTRL = 0xd404;
const AUD_V1_AD = 0xd405;
const AUD_V1_SR = 0xd406;
const AUD_V1_AUX = 0xd407;
const AUD_MODEVOL = 0xd420;
const AUD_FC_LO = 0xd426;
const AUD_FC_HI = 0xd427;
const AUD_FILT_CTRL = 0xd428;
const AUD_FILT_ROUTE = 0xd429;
const AUD_PITCH_CFG = 0xd432;
const TRACKER_SYS_COMMAND = `SYS ${TRACKER_PLAYER_ENTRY.toString(16).toUpperCase()}\r`;
const TRACKER_START_RETRY_DELAY_MS = 250;
const TRACKER_START_MAX_ATTEMPTS = 8;

export type SoundTrackerPlaybackHost = {
  beginAudioDiagnosticsSession(): void;
  endAudioDiagnosticsSession(): void;
  getAudioDiagnostics(): string;
  focusScreen(): void;
  getAudioStatus(): string;
  setActivityStatus(message: string): void;
  startAudio(): Promise<void> | void;
};

export type PlaybackMode = "song" | "pattern" | "write-only";

export type TrackerPlaybackContext = {
  compiled: CompiledTrackerSong;
  loop: boolean;
  mode: PlaybackMode;
  muteMask: number;
  previewPattern: number | null;
  soloMask: number;
  startRow: number;
};

function parsePrg(bytes: Uint8Array): { loadAddress: number; payload: Uint8Array } {
  if (bytes.byteLength < 3) {
    throw new Error("tracker player asset is too small");
  }
  const loadAddress = bytes[0] | (bytes[1] << 8);
  return {
    loadAddress,
    payload: bytes.slice(2)
  };
}

function cloneBytes(bytes: Uint8Array): Uint8Array {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy;
}

export class SoundTrackerPlayback {
  private activeAuditionNote: number | null = null;
  private diagnosticsSessionActive = false;
  private readonly emptyWorkspace = new Uint8Array(TRACKER_WORKSPACE_END - TRACKER_PLAYER_ENTRY + 1);
  private lastContext: TrackerPlaybackContext | null = null;
  private pendingProjectRefresh = false;
  private playerAssetPromise: Promise<Uint8Array> | null = null;
  private refreshPromise: Promise<void> | null = null;
  private startRetryTimer: number | null = null;

  constructor(
    private readonly emulator: Emulator,
    private readonly host: SoundTrackerPlaybackHost
  ) {}

  private async loadPlayerAsset(): Promise<Uint8Array> {
    if (this.playerAssetPromise !== null) {
      return this.playerAssetPromise;
    }
    this.playerAssetPromise = fetch("./assets/tracker_player.prg")
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`failed to fetch tracker player: ${response.status} ${response.statusText}`);
        }
        return new Uint8Array(await response.arrayBuffer());
      })
      .then((bytes) => cloneBytes(bytes));
    return this.playerAssetPromise;
  }

  private queueAscii(text: string): void {
    for (const character of text) {
      let value = character.charCodeAt(0);
      if (value === 0x0a) {
        value = 0x0d;
      }
      if (value >= 0x61 && value <= 0x7a) {
        value -= 0x20;
      }
      this.emulator.pushInputByte(value & 0xff);
    }
  }

  private clearPendingStart(): void {
    if (this.startRetryTimer !== null) {
      window.clearTimeout(this.startRetryTimer);
      this.startRetryTimer = null;
    }
  }

  private queueStartCommand(): void {
    const helper = (window as Window & { __am8DesktopWeb?: { queueInput(text: string): void } }).__am8DesktopWeb;
    if (helper !== undefined) {
      helper.queueInput(TRACKER_SYS_COMMAND);
    } else {
      this.queueAscii(TRACKER_SYS_COMMAND);
    }
  }

  private scheduleStartAttempt(attempt: number): void {
    this.queueStartCommand();
    this.host.setActivityStatus(`Tracker start queued in emulator via ${TRACKER_SYS_COMMAND.trim()}.`);
    this.startRetryTimer = window.setTimeout(() => {
      this.startRetryTimer = null;
      const status = readTrackerEmulatorStatus(this.emulator, this.host.getAudioStatus(), this.host.getAudioDiagnostics());
      if (status.state === TRACKER_STATUS_STATE.playing) {
        return;
      }
      if (attempt + 1 >= TRACKER_START_MAX_ATTEMPTS) {
        this.host.setActivityStatus(`Tracker start did not confirm after ${TRACKER_START_MAX_ATTEMPTS} attempts.`);
        return;
      }
      if (status.state === TRACKER_STATUS_STATE.empty || status.state === TRACKER_STATUS_STATE.written || status.state === TRACKER_STATUS_STATE.stopped) {
        this.scheduleStartAttempt(attempt + 1);
      }
    }, TRACKER_START_RETRY_DELAY_MS);
  }

  private async startPreparedSong(focusScreen: boolean): Promise<void> {
    this.clearPendingStart();
    await this.host.startAudio();
    this.host.beginAudioDiagnosticsSession();
    this.diagnosticsSessionActive = true;
    if (focusScreen) {
      this.host.focusScreen();
    }
    this.scheduleStartAttempt(0);
  }

  private compileProjectForCurrentContext(project: TrackerProject): CompiledTrackerSong {
    if (this.lastContext === null) {
      throw new Error("tracker workspace is not prepared");
    }
    if (this.lastContext.mode === "pattern") {
      return compileTrackerPreview(project, this.lastContext.previewPattern ?? 0);
    }
    return compileTrackerProject(project);
  }

  private writeTransportState(loop: boolean, muteMask: number, soloMask: number): void {
    this.emulator.writeMemory(
      TRACKER_STATUS_ADDRESS + 7,
      new Uint8Array([loop ? 1 : 0, muteMask & 0x0f, soloMask & 0x0f])
    );
  }

  private async rewriteCurrentContext(project: TrackerProject, restartPlayback: boolean): Promise<void> {
    if (this.lastContext === null) {
      return;
    }
    const { loop, mode, muteMask, previewPattern, soloMask, startRow } = this.lastContext;
    this.pendingProjectRefresh = false;
    const compiled = this.compileProjectForCurrentContext(project);
    await this.preparePlayback(compiled, mode, previewPattern, loop, muteMask, soloMask, startRow);
    if (restartPlayback && mode !== "write-only") {
      await this.startPreparedSong(false);
    }
  }

  private writeWorkspace(compiled: CompiledTrackerSong, loop: boolean, muteMask: number, soloMask: number, startRow: number): Promise<void> {
    return this.loadPlayerAsset().then((playerPrg) => {
      const parsed = parsePrg(playerPrg);
      if (parsed.loadAddress !== TRACKER_PLAYER_ENTRY) {
        throw new Error(`tracker player expected load address 0x${TRACKER_PLAYER_ENTRY.toString(16).toUpperCase()} but found 0x${parsed.loadAddress.toString(16).toUpperCase()}`);
      }
      if (parsed.payload.byteLength > TRACKER_STATUS_ADDRESS - TRACKER_PLAYER_ENTRY) {
        throw new Error("tracker player asset overlaps the reserved tracker status block");
      }
      if (compiled.bytecode.byteLength > TRACKER_BYTECODE_MAX_SIZE) {
        throw new Error(`tracker bytecode ${compiled.bytecode.byteLength} exceeds the browser workspace`);
      }

      this.releaseAudition();
      this.clearPendingStart();
      this.emulator.reset();
      this.emulator.writeMemory(TRACKER_PLAYER_ENTRY, this.emptyWorkspace);
      this.emulator.writeMemory(TRACKER_PLAYER_ENTRY, parsed.payload);
      this.emulator.writeMemory(
        TRACKER_STATUS_ADDRESS,
        createTrackerStatusBlock({
          bytecodeSize: compiled.bytecode.byteLength,
          loop,
          muteMask,
          soloMask,
          startOrder: 0,
          startRow,
          tickReload: compiled.tickReload
        })
      );
      this.emulator.writeMemory(TRACKER_BYTECODE_ADDRESS, compiled.bytecode);
      this.host.setActivityStatus("Tracker workspace written to emulator RAM.");
    });
  }

  private async preparePlayback(
    compiled: CompiledTrackerSong,
    mode: PlaybackMode,
    previewPattern: number | null,
    loop: boolean,
    muteMask: number,
    soloMask: number,
    startRow: number
  ): Promise<void> {
    this.pendingProjectRefresh = false;
    await this.writeWorkspace(compiled, loop, muteMask, soloMask, startRow);
    this.lastContext = {
      compiled,
      loop,
      mode,
      muteMask: muteMask & 0x0f,
      previewPattern,
      soloMask: soloMask & 0x0f,
      startRow
    };
  }

  async writeSong(project: TrackerProject, loop: boolean, muteMask: number, soloMask: number): Promise<void> {
    const compiled = compileTrackerProject(project);
    await this.preparePlayback(compiled, "write-only", null, loop, muteMask, soloMask, 0);
  }

  async playSong(project: TrackerProject, loop: boolean, muteMask: number, soloMask: number): Promise<void> {
    await this.host.startAudio();
    const compiled = compileTrackerProject(project);
    await this.preparePlayback(compiled, "song", null, loop, muteMask, soloMask, 0);
    await this.startWrittenSong();
  }

  async playPattern(
    project: TrackerProject,
    activePattern: number,
    startRow: number,
    loop: boolean,
    muteMask: number,
    soloMask: number
  ): Promise<void> {
    await this.host.startAudio();
    const compiled = compileTrackerPreview(project, activePattern);
    await this.preparePlayback(compiled, "pattern", activePattern, loop, muteMask, soloMask, startRow);
    await this.startWrittenSong();
  }

  async startWrittenSong(): Promise<void> {
    await this.startPreparedSong(true);
  }

  syncTransport(loop: boolean, muteMask: number, soloMask: number): void {
    if (this.lastContext === null) {
      return;
    }
    this.lastContext = {
      ...this.lastContext,
      loop,
      muteMask: muteMask & 0x0f,
      soloMask: soloMask & 0x0f
    };
    const status = readTrackerEmulatorStatus(this.emulator, this.host.getAudioStatus(), this.host.getAudioDiagnostics());
    if (!status.valid || !status.workspaceWritten) {
      return;
    }
    this.writeTransportState(loop, muteMask, soloMask);
  }

  queueProjectRefresh(): void {
    if (this.lastContext === null) {
      return;
    }
    this.pendingProjectRefresh = true;
  }

  flushQueuedProjectRefresh(project: TrackerProject, status: TrackerEmulatorStatus): Promise<void> | null {
    if (!this.pendingProjectRefresh || this.lastContext === null || this.refreshPromise !== null) {
      return null;
    }
    if (!status.valid || !status.workspaceWritten) {
      this.pendingProjectRefresh = false;
      return null;
    }
    if (status.state === TRACKER_STATUS_STATE.playing) {
      const atLoopStart =
        status.loop && status.currentOrder === status.startOrder && status.currentRow === status.startRow && status.currentTick === 0;
      if (!atLoopStart) {
        return null;
      }
      this.refreshPromise = this.rewriteCurrentContext(project, true);
    } else {
      this.refreshPromise = this.rewriteCurrentContext(project, false);
    }
    const refresh = this.refreshPromise.finally(() => {
      this.refreshPromise = null;
    });
    return refresh;
  }

  stop(): void {
    this.clearPendingStart();
    this.host.endAudioDiagnosticsSession();
    this.diagnosticsSessionActive = false;
    this.emulator.writeMemory(TRACKER_STATUS_ADDRESS + 10, new Uint8Array([1]));
    this.host.setActivityStatus("Tracker stop requested.");
  }

  readStatus(): TrackerEmulatorStatus {
    const status = readTrackerEmulatorStatus(this.emulator, this.host.getAudioStatus(), this.host.getAudioDiagnostics());
    if (this.diagnosticsSessionActive && this.activeAuditionNote === null && this.startRetryTimer === null && status.state !== TRACKER_STATUS_STATE.playing) {
      this.host.endAudioDiagnosticsSession();
      this.diagnosticsSessionActive = false;
    }
    if (status.valid && this.lastContext !== null) {
      const originalPattern = this.lastContext.compiled.patternMap[status.currentPattern] ?? 0;
      return {
        ...status,
        currentPattern: originalPattern
      };
    }
    return status;
  }

  auditionNote(project: TrackerProject, instrumentIndex: number, noteCode: number): void {
    const instrument = project.song.instruments[instrumentIndex];
    if (instrument === undefined) {
      return;
    }
    this.releaseAudition();
    this.host.startAudio();
    this.host.beginAudioDiagnosticsSession();
    this.diagnosticsSessionActive = true;
    this.host.setActivityStatus("Tracker instrument audition owns the main emulator.");
    this.emulator.writeMemory(AUD_MODEVOL, new Uint8Array([project.song.globals.masterVolume & 0x0f]));
    this.emulator.writeMemory(AUD_FC_LO, new Uint8Array([project.song.globals.filterCutoff & 0xff]));
    this.emulator.writeMemory(AUD_FC_HI, new Uint8Array([(project.song.globals.filterCutoff >> 8) & 0x07]));
    this.emulator.writeMemory(AUD_FILT_CTRL, new Uint8Array([project.song.globals.filterCtrl & 0xff]));
    this.emulator.writeMemory(AUD_FILT_ROUTE, new Uint8Array([instrument.filterRouteEnabled ? 0x01 : 0x00]));
    this.emulator.writeMemory(AUD_PITCH_CFG, new Uint8Array([project.song.globals.pitchMode & 0xff]));

    const frequency = noteCodeToFrequencyRegister(noteCode, project.song.globals.pitchMode);
    this.emulator.writeMemory(AUD_V1_FREQ_LO, new Uint8Array([frequency & 0xff, (frequency >> 8) & 0xff]));
    this.emulator.writeMemory(AUD_V1_PW_LO, new Uint8Array([instrument.pulseWidth & 0xff]));
    this.emulator.writeMemory(AUD_V1_PW_HI, new Uint8Array([(instrument.pulseWidth >> 8) & 0x0f]));
    this.emulator.writeMemory(AUD_V1_AD, new Uint8Array([instrument.ad & 0xff]));
    this.emulator.writeMemory(AUD_V1_SR, new Uint8Array([instrument.sr & 0xff]));
    this.emulator.writeMemory(AUD_V1_AUX, new Uint8Array([instrument.auxRateX8 ? 0x01 : 0x00]));
    this.emulator.writeMemory(AUD_V1_CTRL, new Uint8Array([instrumentCtrlValue(instrument) | 0x01]));
    this.activeAuditionNote = noteCode;
  }

  releaseAudition(): void {
    if (this.activeAuditionNote === null) {
      return;
    }
    this.host.endAudioDiagnosticsSession();
    this.diagnosticsSessionActive = false;
    this.emulator.writeMemory(AUD_V1_CTRL, new Uint8Array([0x00]));
    this.activeAuditionNote = null;
  }
}
